import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { format, parseISO } from "date-fns";
import { formatTime } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from "recharts";
import { Calendar, TrendingUp, Clock, DollarSign, Filter, ArrowUpDown } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface Booking {
  id: string;
  booking_datetime: string; // UTC ISO timestamp
  booking_timezone?: string;
  duration_minutes: number;
  price: number;
  status: string;
  video_conference_link?: string;
  created_at: string;
  payment_status?: string;
  cancellation_fee?: number;
}

const Progress = () => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [sortBy, setSortBy] = useState<string>("created_at_desc");

  useEffect(() => {
    checkAuthAndFetchData();
  }, []);

  const checkAuthAndFetchData = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      navigate("/auth");
      return;
    }

    fetchBookings();
  };

  const fetchBookings = async () => {
    try {
      const { data, error } = await supabase
        .from("bookings")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      setBookings(data || []);
    } catch (error) {
      console.error("Error fetching bookings:", error);
    } finally {
      setLoading(false);
    }
  };

  const nonCancelledBookings = bookings.filter(b => b.status !== 'cancelled');
  const totalLessons = nonCancelledBookings.length;
  const totalMinutes = nonCancelledBookings.reduce((sum, b) => sum + b.duration_minutes, 0);
  const totalSpent = nonCancelledBookings.reduce((sum, b) => sum + Number(b.price), 0);

  const chartData = nonCancelledBookings.slice(0, 6).reverse().map((booking, index) => ({
    name: format(new Date(booking.booking_datetime), "MMM d"),
    duration: booking.duration_minutes,
    price: Number(booking.price)
  }));

  // Filter bookings by status
  const filteredBookings = statusFilter === "all" 
    ? bookings 
    : bookings.filter(b => b.status === statusFilter);

  // Sort bookings
  const sortedBookings = [...filteredBookings].sort((a, b) => {
    switch (sortBy) {
      case "created_at_desc":
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      case "created_at_asc":
        return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      case "booking_date_desc":
        return new Date(b.booking_datetime).getTime() - new Date(a.booking_datetime).getTime();
      case "booking_date_asc":
        return new Date(a.booking_datetime).getTime() - new Date(b.booking_datetime).getTime();
      case "price_desc":
        return Number(b.price) - Number(a.price);
      case "price_asc":
        return Number(a.price) - Number(b.price);
      default:
        return 0;
    }
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case "confirmed":
        return "default";
      case "pending":
        return "secondary";
      case "cancelled":
        return "destructive";
      case "proposed":
        return "outline";
      case "denied":
        return "destructive";
      default:
        return "secondary";
    }
  };

  const statusCounts = {
    all: bookings.length,
    confirmed: bookings.filter(b => b.status === "confirmed").length,
    pending: bookings.filter(b => b.status === "pending").length,
    cancelled: bookings.filter(b => b.status === "cancelled").length,
    proposed: bookings.filter(b => b.status === "proposed").length,
  };

  return (
    <div className="container mx-auto px-4 py-12">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold mb-2 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
            Your Progress
          </h2>
          <p className="text-muted-foreground mb-8">Track your vocal journey</p>

          {loading ? (
            <div className="text-center py-12">Loading...</div>
          ) : (
            <>
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <Card className="border-border/40 shadow-lg">
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Total Lessons</CardTitle>
                    <Calendar className="h-4 w-4 text-primary" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold">{totalLessons}</div>
                    <p className="text-xs text-muted-foreground mt-1">Sessions completed</p>
                  </CardContent>
                </Card>

                <Card className="border-border/40 shadow-lg">
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Practice Time</CardTitle>
                    <Clock className="h-4 w-4 text-primary" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold">{Math.floor(totalMinutes / 60)}h {totalMinutes % 60}m</div>
                    <p className="text-xs text-muted-foreground mt-1">Total training time</p>
                  </CardContent>
                </Card>

                <Card className="border-border/40 shadow-lg">
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Investment</CardTitle>
                    <DollarSign className="h-4 w-4 text-primary" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold">${totalSpent.toFixed(2)}</div>
                    <p className="text-xs text-muted-foreground mt-1">Total spent</p>
                  </CardContent>
                </Card>
              </div>

              {chartData.length > 0 && (
                <div className="grid md:grid-cols-2 gap-6 mb-8">
                  <Card className="border-border/40 shadow-lg">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <TrendingUp className="w-5 h-5 text-primary" />
                        Lesson Duration
                      </CardTitle>
                      <CardDescription>Minutes per session</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={250}>
                        <BarChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                          <XAxis dataKey="name" stroke="hsl(var(--foreground))" fontSize={12} />
                          <YAxis stroke="hsl(var(--foreground))" fontSize={12} />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: 'hsl(var(--card))',
                              border: '1px solid hsl(var(--border))',
                              borderRadius: '8px'
                            }}
                          />
                          <Bar dataKey="duration" fill="hsl(var(--primary))" radius={[8, 8, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  <Card className="border-border/40 shadow-lg">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <DollarSign className="w-5 h-5 text-primary" />
                        Spending Trend
                      </CardTitle>
                      <CardDescription>Investment per session</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={250}>
                        <LineChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                          <XAxis dataKey="name" stroke="hsl(var(--foreground))" fontSize={12} />
                          <YAxis stroke="hsl(var(--foreground))" fontSize={12} />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: 'hsl(var(--card))',
                              border: '1px solid hsl(var(--border))',
                              borderRadius: '8px'
                            }}
                          />
                          <Line 
                            type="monotone" 
                            dataKey="price" 
                            stroke="hsl(var(--primary))" 
                            strokeWidth={3}
                            dot={{ fill: 'hsl(var(--primary))', r: 5 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </div>
              )}

              <Card className="border-border/40 shadow-lg">
                <CardHeader>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                      <CardTitle>My Bookings</CardTitle>
                      <CardDescription>View and manage your lesson history</CardDescription>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <Select value={sortBy} onValueChange={setSortBy}>
                        <SelectTrigger className="w-full sm:w-[180px]">
                          <ArrowUpDown className="w-4 h-4 mr-2" />
                          <SelectValue placeholder="Sort by" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="created_at_desc">Newest First</SelectItem>
                          <SelectItem value="created_at_asc">Oldest First</SelectItem>
                          <SelectItem value="booking_date_desc">Latest Date</SelectItem>
                          <SelectItem value="booking_date_asc">Earliest Date</SelectItem>
                          <SelectItem value="price_desc">Highest Price</SelectItem>
                          <SelectItem value="price_asc">Lowest Price</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <Tabs value={statusFilter} onValueChange={setStatusFilter} className="mb-4">
                    <TabsList className="grid w-full grid-cols-5">
                      <TabsTrigger value="all" className="text-xs">
                        All ({statusCounts.all})
                      </TabsTrigger>
                      <TabsTrigger value="confirmed" className="text-xs">
                        Confirmed ({statusCounts.confirmed})
                      </TabsTrigger>
                      <TabsTrigger value="pending" className="text-xs">
                        Pending ({statusCounts.pending})
                      </TabsTrigger>
                      <TabsTrigger value="proposed" className="text-xs">
                        Proposed ({statusCounts.proposed})
                      </TabsTrigger>
                      <TabsTrigger value="cancelled" className="text-xs">
                        Cancelled ({statusCounts.cancelled})
                      </TabsTrigger>
                    </TabsList>
                  </Tabs>

                  {sortedBookings.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      {statusFilter === "all" 
                        ? "No bookings yet. Book your first lesson to start tracking progress!"
                        : `No ${statusFilter} bookings found.`
                      }
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {sortedBookings.map((booking) => (
                        <div 
                          key={booking.id} 
                          className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 rounded-lg bg-muted/50 border border-border/40 hover:bg-muted cursor-pointer transition-colors gap-3"
                          onClick={() => navigate(`/booking/${booking.id}`)}
                        >
                          <div className="flex-1 space-y-2">
                            <div className="flex flex-wrap items-center gap-2">
                              <Badge variant={getStatusColor(booking.status)} className="capitalize">
                                {booking.status}
                              </Badge>
                              <span className="text-xs text-muted-foreground">
                                Created {format(parseISO(booking.created_at), "MMM d, yyyy")}
                              </span>
                            </div>
                            <div>
                              <p className="font-medium">
                                {format(new Date(booking.booking_datetime), "EEEE, MMMM d, yyyy")}
                              </p>
                              <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground mt-1">
                                <span className="flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {format(new Date(booking.booking_datetime), "h:mm a")}
                                </span>
                                <span>•</span>
                                <span>{booking.duration_minutes} min</span>
                                {booking.video_conference_link && (
                                  <>
                                    <span>•</span>
                                    <span className="text-primary">Has Video Link</span>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-4 sm:flex-col sm:items-end">
                            <div className="text-right">
                              {booking.status === 'cancelled' ? (
                                <>
                                  <p className="font-bold text-lg text-destructive">
                                    ${(booking.cancellation_fee || 0).toFixed(2)}
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                    {booking.cancellation_fee && booking.cancellation_fee > 0 
                                      ? "Cancellation fee" 
                                      : "Fully refunded"}
                                  </p>
                                </>
                              ) : (
                                <>
                                  <p className="font-bold text-lg text-primary">${Number(booking.price).toFixed(2)}</p>
                                  <p className="text-xs text-muted-foreground">{booking.payment_status || "pending"}</p>
                                </>
                              )}
                            </div>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/booking/${booking.id}`);
                              }}
                            >
                              View Details
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </div>
  );
};

export default Progress;
